import React, { useContext, useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  isPlatform,
  useIonRouter,
  useIonAlert,
} from "@ionic/react";
import {
  clearAuthHints,
  clearCachedCredentialIfNotEnrolled,
  getPortalBearerToken,
  handlePWACallback,
  persistBiometricCredentialForEnrollment,
  reusePortalSessionForEam,
  signin,
  signinPortal,
  signoutPortal,
  validateBearerToken,
} from "../util/OAuth";
import {
  isBiometricAvailable,
  isBiometricEnabled,
  setBiometricEnabled,
} from "../util/Biometric";
import { initializeAbpSession } from "../api/abpLogin";
import ApiOptionsModal from "../components/ApiOptionsModal";
import {
  clearApiSession,
  getApiSession,
  mobileBootstrap,
} from "../util/ApiOptionsHelper";
import ApiError from "../components/ApiError";
import { getRequestedURL } from "../util/UrlHelper";
import { TranslationMessagesContext } from "../util/Translations";
import { updateLastAccessDateMobile } from "../api/IAM";
import { Preferences } from "@capacitor/preferences";

const AuthCallback: React.FC = () => {
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [showLoading, setShowLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [inProcess, setInProcess] = useState(false);
  const [isQuestion, setIsQuestion] = useState(false);
  let isModalOpen = false;
  const router = useIonRouter();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    setShowLoading(true);
    if (inProcess) return;

    setInProcess(true);
    validateUser().catch((err) => {
      console.error("Authentication error:", err);
      setShowLoading(false);
      setLoginError(true);
      setLoginErrorMessage("Authentication failed. Please try again.");
      setInProcess(false);
    });
  }, []);

  const validateUser = async () => {
    if (isCallback(window.location.href)) {
      const response = await getAccessTokenFromUrl();
      if (!response.validLogin) {
        if (!isModalOpen) {
          setShowLoading(false);
          setLoginError(true);
          setLoginErrorMessage(response.message);
          if ('resultCode' in response && response.resultCode === 3) {
            setIsQuestion(true);
          }
        }
      } else if(globalThis.isLoginSuccessful){
        const returnPath = await getRequestedURL();
        setTimeout(() => {
          router.push(returnPath);
        }, 2000);
      }
    } else {
      // b2c login or abp login
      const response = await authenticateUser();
      console.log('response: ', response);
      setInProcess(false);
      if (!response.validLogin) {
        if (!isModalOpen) {
          console.log(
            "validateUser - authenticateUser: User login failed in auth callback"
          );
          setShowLoading(false);
          setLoginError(true);
          setLoginErrorMessage(response.message)
          if ('resultCode' in response && response.resultCode === 3) {
            setIsQuestion(true);
          }
        }
      } else if(globalThis.isLoginSuccessful){
        const returnPath = await getRequestedURL();
        setTimeout(() => {
          router.push(returnPath);
        }, 2000);
      }
    }
  };

  const isCallback = (url: string) => {
    if (
      !!url &&
      url.indexOf("state") > -1 &&
      url.indexOf("access_token") > -1
    ) {
      return true;
    }
    return false;
  };

  const getAccessTokenFromUrl = async () => {
    const response = await handlePWACallback(window.location.href, false);
    if (!response) {
      return {validLogin: false, message: ''};
    }

    //abplogin
    return await validateABPLicense(true);
  };

  const authenticateUser = async () => {
    //biometric fast path: the user chose biometric on the landing page and the
    //session was already restored there, so skip BOTH B2C round-trips (portal +
    //EAM) and the cached plant/DB, and go straight to license validation.
    if (sessionStorage.getItem("biometricRestored") === "true") {
      sessionStorage.removeItem("biometricRestored");
      return await validateABPLicense(false);
    }

    //check if portal authentication is needed
    const portalToken = await getPortalBearerToken();
    if (!!!portalToken) {
      setLoginError(false);
      setLoginErrorMessage('');
      //authenticate user in portal
      await signinPortal();
    }

    //check if mobileBootstrap data is available
    try {
      await mobileBootstrap();
    } catch (err) {
      console.error("mobileBootstrap failed, continuing with cached data:", err);
    }

    //logout of portal
    await signoutPortal();
    
    //check for plant/db - apiSession
    if (await checkApiSession()) {
      return validateEAMSession();
    }
    return {validLogin: false, message: ""};
  };

  const validateEAMSession = async () => {
    //check b2c on eam
    if (!await validateBearerToken()) {
      //the portal sign-in moments ago already produced an equivalent token on
      //tenants where both flows share a client+scope - adopt it rather than
      //prompting the user for the same credentials a second time
      if (!await reusePortalSessionForEam()) {
        //b2c authentication on eam
        const signedIn = await signin();
        if (!signedIn) {
          return { validLogin: false, message: ""};
        }
      }
      //fresh full login by either route - offer to enable biometric sign-in for
      //next time. The reuse path stores the same credential signin() would have,
      //so the offer stays available rather than being skipped on that path.
      await maybeOfferBiometricEnroll();
    }
    //abplogin
    return await validateABPLicense(false);
  };

  /**
   * After a fresh B2C login, offer to enable biometric sign-in if the device
   * supports it and the user has not already opted in. Awaits the user's choice.
   */
  const maybeOfferBiometricEnroll = async () => {
    try {
      if (await isBiometricEnabled()) {
        return;
      }
      const { available, label } = await isBiometricAvailable();
      if (!available) {
        // No biometric hardware/enrollment: drop the token cached at login so we
        // never retain a credential the user can never use.
        await clearCachedCredentialIfNotEnrolled();
        return;
      }
      const optedIn = await new Promise<boolean>((resolve) => {
        setShowLoading(false);
        presentAlert({
          header: `Enable ${label} sign-in?`,
          message: `Use ${label} to sign in next time instead of entering your credentials.`,
          buttons: [
            {
              text: translatedMessages["lbl_btn_no"]?.MessageText || "Not now",
              role: "cancel",
              handler: () => resolve(false),
            },
            {
              text: translatedMessages["lbl_btn_yes"]?.MessageText || "Enable",
              handler: () => resolve(true),
            },
          ],
          onDidDismiss: () => resolve(false),
        });
      });
      if (!optedIn) {
        // User declined: remove the token cached at login so we never keep a
        // credential for someone who did not opt in.
        await clearCachedCredentialIfNotEnrolled();
        return;
      }
      //store the credential first; only turn on the opt-in if it actually saved,
      //so the "Sign in with biometric" button never appears without a credential.
      const stored = await persistBiometricCredentialForEnrollment();
      if (stored) {
        await setBiometricEnabled(true);
      } else {
        console.log("maybeOfferBiometricEnroll: credential not stored, not enabling biometric");
        await clearCachedCredentialIfNotEnrolled();
      }
      setShowLoading(true);
    } catch (err) {
      console.log("maybeOfferBiometricEnroll error:", err);
    }
  };

  const checkApiSession = async () => {
    const apiSession = await getApiSession();
    if (
      !!apiSession &&
      !!apiSession?.PlantCode &&
      !!apiSession?.DatabaseName &&
      !!apiSession.BackendApiUrl &&
      !!apiSession.B2CUserFlowScope
    ) {
      return true;
    }
    if (!isModalOpen) {
      setIsOpen(true);
      isModalOpen = true;
      setShowLoading(false);
    }
    return false;
  };

  const validateABPLicense = async (isDirectLaunch: boolean = false) => {
    const abpLogin = await initializeAbpSession();
    if (!abpLogin.validLogin  && 'resultCode' in abpLogin && abpLogin.resultCode !== 3) {
      //clear the api options selection
      await clearApiSession();
    }
    if (abpLogin.validLogin && isDirectLaunch) {
      // Update last access date only when user opens the app directly (not from portal)
      try {
        const userEmail = sessionStorage.getItem('UserLoginEmail');
        if (userEmail) {
          sessionStorage.removeItem('UserLoginEmail');
          await updateLastAccessDateMobile(userEmail);
        }
      } catch (error) {
        console.log('updateLastAccessDateMobile failed:', error);
      }
    }
    return abpLogin;
  };

  const confirmApiOptions = async (cancel: boolean) => {
    setShowLoading(true);

    //close modal
    setIsOpen(false);
    isModalOpen = false;
    //redirect to logout if cancelled
    if (cancel) {
      if (isPlatform('hybrid')) {
        // On mobile, OAuth2Client.logout() triggers a deep-link redirect to /AuthCallback
        // before Preferences.clear() runs in Logout.tsx, so _iamKey survives and
        // checkIAMCacheExists() skips UserIdentity entirely. Bypass the logout route:
        // clear state here directly, set forceLoginPrompt so signinPortal() forces
        // fresh B2C credentials instead of silently re-authenticating via SSO.
        sessionStorage.setItem('forceLoginPrompt', 'true');
        //this path bypasses Logout.tsx, so drop the identity hints here too -
        //otherwise forceLoginPrompt is undermined by a stale login_hint
        clearAuthHints();
        await Preferences.clear();
        window.location.href = "/";
        return false;
      }
      globalThis.relogin = true;
      router.push("/logout");
      return false;
    } else {
      // Session was already validated by ApiOptionsModal.processOptionsChange()
      // before confirmCallback(false) was called - re-validating here triggers a
      // second abpLogin() -> second actionUpdateAuditLog() -> duplicate activity log entry on mobile.
      const returnPath = await getRequestedURL();
      setTimeout(() => {
        setShowLoading(false);
        router.push(returnPath);
      }, 2000);
      return true;
    }
  };

  return (
    <IonPage>
      <IonLoading
        cssClass="my-custom-class"
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={`${
          translatedMessages["ValUser"]?.MessageText || "Validating user"
        }...`}
        duration={15000}
      />
      <IonHeader>
        <IonToolbar>
          <IonTitle>EAM Mobile</IonTitle>
        </IonToolbar>
      </IonHeader>
      <ApiError
        errorData={
          loginError
            ? (loginErrorMessage === ""
                ? translatedMessages["LoginFailed"]?.MessageText || "Login failed, try again after restarting the app."
                : loginErrorMessage
              )
            : ""
        }  showRelogin={!isQuestion}
        showCancel={isQuestion}
        showOk={isQuestion}
        setShowLoading={setShowLoading}
      />
      <IonContent className="ion-padding">
        <ApiOptionsModal
          isOpen={isOpen}
          isInit={true}
          confirmCallback={confirmApiOptions}
        />
      </IonContent>
    </IonPage>
  );
};

export default AuthCallback;
